// Agenda — two views:
//   • Day: the working view. A quick week strip and date banner, a Schedule box (shareable entries with
//     an inline "done" tick) and a Notes box (rich-text day notes, auto-saved).
//   • Archive: a searchable personal history of your own past notes and entries. Open any day from it.
// Styles live in global.css (agenda-*), so nothing is injected per render.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ENTRY_COLORS, type AgendaEntry, type AgendaEntryInput } from '@technohull/shared';
import {
  useAgenda,
  useAgendaArchive,
  useAgendaUpcoming,
  useDeleteAgendaEntry,
  useSaveAgendaEntry,
  useSaveAgendaNote,
} from '../features/hooks.js';
import { useNameResolver } from '../features/directory.js';
import { useAuth } from '../auth/AuthContext.js';
import { ErrorState, FilterBar, InlineError, LoadingState, Modal, PageHeader, SearchInput, Segmented, SyncLabel } from '../components/ui.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { PeoplePicker } from '../components/PeoplePicker.js';
import { htmlToText, looksLikeHtml, sanitizeHtml } from '../lib/richtext.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { IconCheck } from '../components/icons.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

const todayIso = localTodayIso;
function shiftDate(date: string, delta: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}
/** Monday of the week containing `date`. */
function weekStart(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

function entryToInput(e: AgendaEntry): AgendaEntryInput {
  return { id: e.id, date: e.date, time: e.time, endTime: e.endTime, endDate: e.endDate, title: e.title, text: e.text, color: e.color, done: e.done, sharedWith: e.sharedWith };
}

// spoken names for the entry colour swatches (a numbered fallback covers any colour added later).
const COLOUR_NAMES: Record<string, string> = { '#1c93a4': 'Teal', '#2e7d52': 'Green', '#b8321f': 'Red', '#cdd4d8': 'Grey', '#6b3fa0': 'Purple', '#d97706': 'Amber' };

/** a padlock (Feather style) in place of the done tick on someone else's entry, on touch screens. */
function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

// ── someone else's entry: a read-only summary, not a disabled form ─────────────────
function EntrySummary({ entry }: { entry: AgendaEntry }) {
  const df = useDateFormatter();
  const nameOf = useNameResolver();
  const day = (iso: string) => df.date(iso, { weekday: 'short' });
  const multi = !!entry.endDate && entry.endDate !== entry.date;
  // empty end fields are simply skipped: "Mon 05 Oct 2026 · 13:30", "… · 09:00-11:00", "… → Wed 07 Oct 2026"
  const time = entry.time ? (entry.endTime && !multi ? `${entry.time}-${entry.endTime}` : entry.time) : entry.endTime && !multi ? `until ${entry.endTime}` : '';
  const when = [day(entry.date), time].filter(Boolean).join(' · ') + (multi ? ` → ${day(entry.endDate!)}${entry.endTime ? ` · ${entry.endTime}` : ''}` : '');
  return (
    <div className="ae-sum">
      <div className="ae-sum-title">
        <span className="ae-sum-dot" style={{ background: entry.color || 'var(--accent)' }} aria-hidden="true" />
        <span>{entry.title || 'Untitled'}</span>
      </div>
      <div className="ae-sum-when">{when}{entry.done ? ' · done' : ''}</div>
      {entry.text && (looksLikeHtml(entry.text)
        ? <div className="ae-sum-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(entry.text) }} />
        : <div className="ae-sum-text">{entry.text}</div>)}
      <div className="ae-sum-share">
        <span className="muted">Shared by</span>
        <span className="ppick-chip">{nameOf(entry.owner)}</span>
        {entry.sharedWith.length > 0 && (
          <>
            <span className="muted">with</span>
            {entry.sharedWith.map((e) => <span key={e} className="ppick-chip">{nameOf(e)}</span>)}
          </>
        )}
        <span className="muted">- read only</span>
      </div>
    </div>
  );
}

// ── entry add / edit modal ─────────────────────────────────────────────────────
function EntryModal({ date, entry, myEmail, onClose }: { date: string; entry: AgendaEntry | null; myEmail: string; onClose: () => void }) {
  const save = useSaveAgendaEntry();
  const del = useDeleteAgendaEntry();
  const [title, setTitle] = useState(entry?.title ?? '');
  const [time, setTime] = useState(entry?.time ?? '');
  const [endTime, setEndTime] = useState(entry?.endTime ?? '');
  const [endDate, setEndDate] = useState(entry?.endDate ?? '');
  const [color, setColor] = useState(entry?.color ?? ENTRY_COLORS[0]!);
  const [text, setText] = useState(entry?.text ?? '');
  const [shared, setShared] = useState<string[]>(entry?.sharedWith ?? []);
  const [done, setDone] = useState(entry?.done ?? false);
  const readOnly = !!entry && entry.owner !== myEmail;

  const submit = () => {
    save.mutate({ id: entry?.id, date: entry?.date ?? date, time, endTime, endDate, title, text, color, done, sharedWith: shared }, { onSuccess: onClose });
  };

  return (
    <Modal
      title={entry ? (readOnly ? 'Shared entry' : 'Edit entry') : 'Add entry'}
      onClose={onClose}
      footer={
        readOnly ? (
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        ) : (
          <>
            {entry && (
              <button className="btn btn-danger btn-sm" style={{ marginRight: 'auto' }} disabled={del.isPending} onClick={() => del.mutate(entry.id, { onSuccess: onClose })}>{del.isPending ? 'Deleting…' : 'Delete'}</button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!title.trim() || save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save'}</button>
          </>
        )
      }
    >
      {readOnly ? <EntrySummary entry={entry!} /> : (
        <>
          <label className="field-label" htmlFor="ae-title">Title</label>
          <input id="ae-title" className="field" style={{ width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="ae-times">
            <div><label className="field-label" htmlFor="ae-time">Time</label><input id="ae-time" className="field" style={{ width: '100%' }} type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            <div><label className="field-label" htmlFor="ae-end-time">End time</label><input id="ae-end-time" className="field" style={{ width: '100%' }} type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div>
            <div><label className="field-label" htmlFor="ae-end-date">End date</label><input id="ae-end-date" className="field" style={{ width: '100%' }} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          </div>
          <label className="field-label" style={{ marginTop: 10 }}>Colour</label>
          <div className="ae-swatches" role="group" aria-label="Colour">
            {ENTRY_COLORS.map((c, i) => (
              <button key={c} type="button" className="ae-swatch" aria-label={COLOUR_NAMES[c] ?? `Colour ${i + 1}`} aria-pressed={color === c} onClick={() => setColor(c)} style={{ background: c }} />
            ))}
          </div>
          <label className="field-label" style={{ marginTop: 10 }}>Details</label>
          <RichTextEditor value={text} onChange={setText} placeholder="Details…" ariaLabel="Details" minHeight={80} />
          <label className="field-label" style={{ marginTop: 10 }}>Mention / share with</label>
          <PeoplePicker value={shared} onChange={setShared} placeholder="Type a colleague’s name…" ariaLabel="Mention / share with" />
          <label className="check-row ae-done">
            <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} /> Done
          </label>
        </>
      )}
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      {del.isError && <InlineError error={del.error} onDismiss={() => del.reset()} />}
    </Modal>
  );
}

// ── Day view ────────────────────────────────────────────────────────────────────
function AgendaDay({ date, setDate, myEmail }: { date: string; setDate: (d: string) => void; myEmail: string }) {
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const coarse = useIsCoarse();
  const { data, isLoading, error, refetch } = useAgenda(date);
  const saveNote = useSaveAgendaNote();
  const saveEntry = useSaveAgendaEntry();
  const [modal, setModal] = useState<{ entry: AgendaEntry | null } | null>(null);
  // a local flag, not isFetching: every autosave/tick invalidates ['agenda'], which would flicker the button.
  const [syncing, setSyncing] = useState(false);

  // week strip: which of the 7 days around `date` have entries.
  const wkStart = useMemo(() => weekStart(date), [date]);
  const wkEnd = useMemo(() => shiftDate(wkStart, 6), [wkStart]);
  const week = useAgendaUpcoming(wkStart, wkEnd);
  const daysWithEntries = useMemo(() => {
    const set = new Set<string>();
    for (const e of week.data?.entries ?? []) {
      const end = e.endDate && e.endDate > e.date ? e.endDate : e.date;
      for (let d = e.date; d <= end && d <= wkEnd; d = shiftDate(d, 1)) if (d >= wkStart) set.add(d);
    }
    return set;
  }, [week.data, wkStart, wkEnd]);

  // notes textarea with debounced autosave.
  const [note, setNote] = useState('');
  useEffect(() => setNote(data?.note ?? ''), [data?.note, date]);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // dirty = typed but not yet confirmed saved (covers the debounce too). only the newest keystroke's
  // save may clear it, so an older request settling never reads "Auto-saved" for unsent text.
  const [dirty, setDirty] = useState(false);
  const seq = useRef(0);
  const onNote = (v: string) => {
    setNote(v);
    setDirty(true);
    const n = ++seq.current;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveNote.mutate({ date, text: v }, { onSettled: () => { if (seq.current === n) setDirty(false); } }), 700);
  };

  const entries = useMemo(() => [...(data?.entries ?? [])].sort((a, b) => (a.time || '99').localeCompare(b.time || '99')), [data]);
  const toggleDone = (e: AgendaEntry) => { if (e.owner === myEmail) saveEntry.mutate({ ...entryToInput(e), done: !e.done }); };

  const today = todayIso();
  const weekday = df.weekday(date, 'long');
  const isToday = date === today;

  return (
    <>
      {/* phones: ‹ [date] › on one row, then Today, + Entry (the wide one) and Sync; .agenda-daybar-acts is
          display:contents elsewhere, so the desktop bar is one flat row as before */}
      <div className="filterbar agenda-daybar" style={{ marginBottom: 12 }}>
        <button className="btn btn-sm agenda-step" onClick={() => setDate(shiftDate(date, -1))} aria-label="Previous day">‹</button>
        <input className="field" type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} />
        <button className="btn btn-sm agenda-step" onClick={() => setDate(shiftDate(date, 1))} aria-label="Next day">›</button>
        <div className="agenda-daybar-acts">
          <button className="btn btn-sm" onClick={() => setDate(today)}>Today</button>
          <span className="agenda-daybar-sp" style={{ flex: 1 }} />
          <button className="btn btn-sm btn-primary" onClick={() => setModal({ entry: null })}>+ Entry</button>
          <button
            className="btn btn-sm"
            disabled={syncing}
            onClick={async () => { setSyncing(true); try { await Promise.all([refetch(), week.refetch()]); } finally { setSyncing(false); } }}
          ><SyncLabel busy={syncing} /></button>
        </div>
      </div>

      <div className="agenda-week">
        {Array.from({ length: 7 }, (_, i) => shiftDate(wkStart, i)).map((dayIso) => {
          return (
            <button
              key={dayIso}
              type="button"
              className={`agenda-wk${dayIso === date ? ' sel' : ''}${dayIso === today ? ' today' : ''}`}
              aria-pressed={dayIso === date}
              aria-current={dayIso === today ? 'date' : undefined}
              onClick={() => setDate(dayIso)}
            >
              <span className="agenda-wk-wd">{df.weekday(dayIso)}</span>
              <span className="agenda-wk-day">{Number(dayIso.slice(8))}</span>
              <span className={`agenda-wk-dot${daysWithEntries.has(dayIso) ? ' on' : ''}`} aria-hidden="true" />
              {daysWithEntries.has(dayIso) && <span className="sr-only">has entries</span>}
            </button>
          );
        })}
      </div>

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <div className="agenda-wrap">
          {/* the day, as one line on a quiet band (phones shorten the weekday: the week strip shows it) */}
          <div className="agenda-hd">
            <div className="agenda-hd-date">{isToday ? 'Today · ' : ''}{isPhone ? df.weekday(date) : weekday} {fmtDate(date)}</div>
            <div className="agenda-hd-count">{entries.length === 0 ? 'No items' : `${entries.length} item${entries.length === 1 ? '' : 's'}`}</div>
          </div>

          {saveEntry.isError && <InlineError error={saveEntry.error} onDismiss={() => saveEntry.reset()} />}
          <div className="agenda-grid">
            <section className="agenda-box">
              <div className="agenda-box-hd">Schedule</div>
              <div className="agenda-box-bd">
                {entries.length === 0 && <div className="agenda-empty">Nothing scheduled for this day.</div>}
                {entries.map((e) => {
                  const range = e.time ? `${e.time}${e.endTime ? `-${e.endTime}` : ''}` : '-';
                  const mine = e.owner === myEmail;
                  return (
                    <div key={e.id} className={`agenda-entry ${e.done ? 'done' : ''}`}>
                      {!mine && coarse ? (
                        // touch has no tooltip to explain a greyed-out tick, so say it: a lock (+ the "shared" badge)
                        <span className="agenda-lock" role="img" aria-label="Shared entry, read only"><LockIcon /></span>
                      ) : (
                        <button
                          type="button"
                          className={`agenda-done${e.done ? ' on' : ''}`}
                          disabled={!mine || (saveEntry.isPending && saveEntry.variables?.id === e.id)}
                          title={mine ? (e.done ? 'Mark not done' : 'Mark done') : 'Shared entry'}
                          onClick={() => toggleDone(e)}
                        >
                          {e.done && <IconCheck size={12} />}
                        </button>
                      )}
                      <span className={`agenda-entry-time${e.time ? '' : ' is-empty'}`}>{range}</span>
                      {/* the whole row opens the entry (a stretched ::after; the tick sits above it) */}
                      <div className="agenda-entry-open" role="button" tabIndex={0} onClick={() => setModal({ entry: e })} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setModal({ entry: e }); } }}>
                        <div className="agenda-entry-title">
                          {/* the entry's colour rides as a dot in front of its title (no side stripe); one inline run,
                              so a long title wraps under its dot instead of leaving it alone on a line */}
                          <span className="agenda-entry-name"><span className="agenda-entry-dot" style={{ background: e.color || 'var(--accent)' }} aria-hidden="true" />{e.title}</span>
                          {!mine && <span className="agenda-badge">shared</span>}
                          {e.endDate && e.endDate !== e.date && <span className="agenda-badge">→ {fmtDate(e.endDate)}</span>}
                        </div>
                        {e.text && (looksLikeHtml(e.text)
                          ? <div className="agenda-entry-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(e.text) }} />
                          : <div className="agenda-entry-text">{e.text}</div>)}
                      </div>
                    </div>
                  );
                })}
                <button className="agenda-add" onClick={() => setModal({ entry: null })}>+ Add entry</button>
              </div>
            </section>

            <section className="agenda-box">
              <div className="agenda-box-hd">Notes</div>
              <div className="agenda-box-bd">
                <RichTextEditor key={date} className="agenda-notes-rte" value={note} onChange={onNote} placeholder="Notes for the day…" ariaLabel="Notes" />
                {/* only the failure is announced; the Saving…/Auto-saved flip at every pause would be chatty */}
                <div className={`agenda-save${saveNote.isError ? ' err' : ''}`}>
                  {saveNote.isError
                    ? <span role="alert">Not saved - keep typing to retry</span>
                    : dirty || saveNote.isPending ? 'Saving…' : note ? 'Auto-saved' : ''}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {modal && <EntryModal date={date} entry={modal.entry} myEmail={myEmail} onClose={() => setModal(null)} />}
    </>
  );
}

// ── Archive view (personal, searchable) ──────────────────────────────────────────
function ArchiveList({ openDay, onCount }: { openDay: (date: string) => void; onCount: (days: number | null) => void }) {
  const fmtDate = useFormatDate();
  const { data, isLoading, isFetching, error, refetch } = useAgendaArchive(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'notes' | 'entries'>('all');

  const rows = useMemo(() => {
    const byDate = new Map<string, { note: string; entries: AgendaEntry[] }>();
    for (const n of data?.notes ?? []) {
      const cur = byDate.get(n.date) ?? { note: '', entries: [] };
      cur.note = n.text;
      byDate.set(n.date, cur);
    }
    for (const e of data?.entries ?? []) {
      const cur = byDate.get(e.date) ?? { note: '', entries: [] };
      cur.entries.push(e);
      byDate.set(e.date, cur);
    }
    let list = [...byDate.entries()].map(([date, v]) => ({ date, note: v.note, entries: v.entries }));
    if (filter === 'notes') list = list.filter((r) => r.note.trim() !== '');
    else if (filter === 'entries') list = list.filter((r) => r.entries.length > 0);
    const query = fold(q.trim());
    if (query) {
      list = list.filter((r) =>
        hasText(query, r.date) ||
        hasText(query, htmlToText(r.note)) ||
        r.entries.some((e) => hasText(query, e.title + ' ' + htmlToText(e.text))),
      );
    }
    return list.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [data, q, filter]);

  // the day count reads in the page header (.page-count), so it stays visible while the filters are folded
  const days = data ? rows.length : null;
  useEffect(() => { onCount(days); }, [days, onCount]);
  useEffect(() => () => onCount(null), [onCount]);

  return (
    <div className="agenda-arch">
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search notes and entries…" />} activeCount={filter === 'all' ? 0 : 1}>
        <Segmented
          value={filter}
          onChange={setFilter}
          ariaLabel="Filter"
          options={[{ value: 'all', label: 'All' }, { value: 'notes', label: 'Notes' }, { value: 'entries', label: 'Entries' }]}
        />
        <span style={{ flex: 1 }} />
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} /></button>
      </FilterBar>

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <div className="agenda-arch-list">
          {rows.length === 0 && <div className="agenda-arch-empty">{q ? 'Nothing matches your search.' : 'Your saved notes and entries will appear here.'}</div>}
          {rows.map((r) => (
            <button key={r.date} type="button" className="agenda-arch-row" onClick={() => openDay(r.date)} title="Open this day">
              <span className="agenda-arch-date">{fmtDate(r.date)}</span>
              <span className="agenda-arch-body">
                {filter !== 'entries' && r.note.trim() !== '' && <span className="agenda-arch-note">{htmlToText(r.note)}</span>}
                {filter !== 'notes' && r.entries.length > 0 && (
                  <span className="agenda-arch-chips">
                    {r.entries.slice(0, 8).map((e) => (
                      <span key={e.id} className={`agenda-badge${e.done ? ' done' : ''}`}>{e.title || 'Untitled'}</span>
                    ))}
                    {r.entries.length > 8 && <span className="agenda-badge">+{r.entries.length - 8}</span>}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── page shell ───────────────────────────────────────────────────────────────────
export function Agenda() {
  const { me } = useAuth();
  const myEmail = me?.user.email ?? '';
  const [view, setView] = useState<'day' | 'archive'>('day');
  const [date, setDate] = useState(todayIso);
  const [archDays, setArchDays] = useState<number | null>(null);

  return (
    <>
      <PageHeader title="Agenda">
        {view === 'archive' && archDays != null && (
          <span className="muted page-count" style={{ fontSize: 12 }}>{archDays} day{archDays === 1 ? '' : 's'}</span>
        )}
        <Segmented value={view} onChange={setView} ariaLabel="View" options={[{ value: 'day', label: 'Day' }, { value: 'archive', label: 'Archive' }]} />
      </PageHeader>

      {view === 'day'
        ? <AgendaDay date={date} setDate={setDate} myEmail={myEmail} />
        : <ArchiveList openDay={(d) => { setDate(d); setView('day'); }} onCount={setArchDays} />}
    </>
  );
}
